import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { getArtistDetails, getArtistDetailsPublic } from '../api/api';
import { BlendedArtistListItem } from 'shared/types/platformTypes';
import { AuthContext } from '../components/Auth/AuthContext/AuthContext';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import logger from '../utils/logger';

const MAX_RETRIES = 4;
const RETRY_DELAY = 5000; // 5 seconds

const useArtistFetchQueue = (
  onArtistsFetched: (artists: BlendedArtistListItem[]) => void,
  apiClient = { getArtistDetails, getArtistDetailsPublic }
) => {
    const [pendingArtistQueue, setPendingArtistQueue] = useState<Map<string, number>>(new Map());
    const isProcessingRef = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { currentUser } = useContext(AuthContext);
    
    const fetchAndUpdateArtist = useCallback(async (artistName: string) => {
        try {
            const artistDetails = currentUser
                ? await apiClient.getArtistDetails(artistName) as BlendedArtistListItem[]
                : await apiClient.getArtistDetailsPublic(undefined, artistName, undefined, true) as BlendedArtistListItem[];

            if (artistDetails.length > 0) {
                onArtistsFetched(artistDetails);
                return true;
            }
        } catch (error) {
            logger.error(`Error fetching details for ${artistName}:`, error);
        }

        return false;
    }, [apiClient, onArtistsFetched, currentUser]);

    const addArtistToQueue = useCallback(async (artistName: string) => {
        const success = await fetchAndUpdateArtist(artistName);

        if (!success) {
            setPendingArtistQueue(prevQueue => {
                const newQueue = new Map(prevQueue);
                newQueue.set(artistName, 1); // Start with retry count 1
                return newQueue;
            });
        }
    }, [fetchAndUpdateArtist]);

    const processQueue = useCallback(async () => {
        if (isProcessingRef.current) {
            return;
        }

        isProcessingRef.current = true;

        const currentQueue = new Map(pendingArtistQueue);
        let hasChanges = false;

        for (const [artistName, retryCount] of currentQueue) {
            if (retryCount >= MAX_RETRIES) {
                onArtistsFetched([{
                    artistName,
                    artistListItemStatus: 'not found',
                    artistListItemActive: true,
                } as BlendedArtistListItem]);
                currentQueue.delete(artistName);
                hasChanges = true;

                // Track the ARTIST_NOT_FOUND event
                trackEvent(EventName.ARTIST_NOT_FOUND, {
                    userId: currentUser?.userId,
                    artistName,
                    retryCount: MAX_RETRIES,
                });

                continue;
            }

            const success = await fetchAndUpdateArtist(artistName);
            if (success) {
                currentQueue.delete(artistName);
            } else {
                currentQueue.set(artistName, retryCount + 1);
            }
            hasChanges = true;
        }

        isProcessingRef.current = false;

        if (hasChanges) {
            setPendingArtistQueue(currentQueue);
        }
    }, [pendingArtistQueue, fetchAndUpdateArtist, onArtistsFetched, currentUser]);

    useEffect(() => {
        const scheduleNextProcess = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            const itemsToProcess = Array.from(pendingArtistQueue.values()).filter(retryCount => retryCount <= MAX_RETRIES).length;

            if (itemsToProcess > 0 && !isProcessingRef.current) {
                timeoutRef.current = setTimeout(() => {
                    processQueue().then(scheduleNextProcess);
                }, RETRY_DELAY);
            }
        };

        scheduleNextProcess();

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [pendingArtistQueue, processQueue]);

    return {
        addArtistToQueue,
    };
};

export default useArtistFetchQueue;